import { PortableText } from '@portabletext/react';
import { Children, useMemo } from 'react';
import { Platform, StyleSheet, View, type ViewProps } from 'react-native';

import type { PageBuilderSimpleBlockContent } from '@fhs/cms-groq-queries';
import { type HeadingTextProps, type ParagraphTextProps, Text } from '@fhs/ui';

import { ActionHandler } from '../types';
import { addActionSource } from '../utils/action';
import {
  plainTextFromPortableBlock,
  portableBlockContainsLinks,
} from '../utils/plain-text-from-block-content';

import { ActionLink } from './action-link';

/** RN Web: VoiceOver/Safari often skip plain Text nodes; use a named region + tab stop (not role=heading, to avoid “heading level N” chatter). */
function webAccessibleTextRegionProps(label: string): Partial<ViewProps> {
  if (Platform.OS !== 'web' || !label.trim()) {
    return {};
  }
  return {
    role: 'region',
    'aria-label': label.trim(),
    tabIndex: 0,
  } as Partial<ViewProps>;
}

type SimpleBlockContentProps = {
  value: PageBuilderSimpleBlockContent;
  onAction: ActionHandler;
  linkText?: Partial<ParagraphTextProps>;
} & (
  | { normalTextType?: 'paragraph'; normalText?: Partial<ParagraphTextProps> }
  | { normalTextType?: 'heading'; normalText?: Partial<HeadingTextProps> }
);

export function SimpleBlockContent({
  normalTextType = 'paragraph',
  ...props
}: SimpleBlockContentProps) {
  return (
    <PortableText
      value={props.value}
      components={useMemo(
        () => ({
          block: {
            normal: ({ children, value }) => {
              const plainLabel = plainTextFromPortableBlock(value);
              const hasLinks = portableBlockContainsLinks(value);
              const useMergedAnnouncement = Boolean(plainLabel) && !hasLinks;

              if (normalTextType === 'paragraph') {
                if (useMergedAnnouncement) {
                  return (
                    <View
                      style={styles.normalTextWrapper}
                      accessible
                      accessibilityLabel={plainLabel}
                      accessibilityRole="text"
                      {...webAccessibleTextRegionProps(plainLabel)}
                    >
                      <Text.Paragraph
                        accessible={false}
                        importantForAccessibility="no"
                        size="md"
                        {...props.normalText}
                      >
                        {children}
                      </Text.Paragraph>
                    </View>
                  );
                }
                return (
                  <View style={styles.normalTextWrapper}>
                    <Text.Paragraph size="md" {...props.normalText}>
                      {children}
                    </Text.Paragraph>
                  </View>
                );
              }

              if (useMergedAnnouncement) {
                return (
                  <View
                    style={styles.normalTextWrapper}
                    accessible
                    accessibilityLabel={plainLabel}
                    accessibilityRole="text"
                    {...webAccessibleTextRegionProps(plainLabel)}
                  >
                    <Text.Heading
                      accessible={false}
                      importantForAccessibility="no"
                      type="two"
                      {...props.normalText}
                    >
                      {children}
                    </Text.Heading>
                  </View>
                );
              }

              return (
                <View style={styles.normalTextWrapper}>
                  <Text.Heading type="two" {...props.normalText}>
                    {children}
                  </Text.Heading>
                </View>
              );
            },
          },
          marks: {
            link: ({ children, value }) => {
              return (
                <ActionLink
                  action={value}
                  onAction={addActionSource(
                    {
                      text: Children.toArray(children)
                        .filter(child => typeof child === 'string')
                        .join(', '),
                    },
                    props.onAction
                  )}
                >
                  <Text.Paragraph size="md" {...props.linkText}>
                    {children}
                  </Text.Paragraph>
                </ActionLink>
              );
            },
          },
        }),
        [normalTextType, props.normalText, props.onAction, props.linkText]
      )}
    />
  );
}

const styles = StyleSheet.create({
  normalTextWrapper: {
    maxWidth: '100%',
  },
});
